import { useMutation } from '@tanstack/react-query'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  getRecentSearchesClient,
  makeOptionallyAuthenticatedQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type {
  CreateRecentSearchDto,
  PaginatedRecentSearchDto,
} from '@b-stock/saved-search-api-client'

type Return = {
  status: number
  statusText: string
}

type RecentSearchParams = {
  limit?: number
  accountId?: string
  personId?: string
}

const getRecentSearchData = async (
  accessToken: string | null,
  { limit, accountId, personId }: RecentSearchParams
): Promise<PaginatedRecentSearchDto> => {
  try {
    const client = getRecentSearchesClient()
    const axiosParams = buildAxiosRequestConfig({ accessToken })

    const response = await client.getRecentSearches(
      { limit, accountId, personId },
      axiosParams
    )

    return response.data
  } catch (_error) {
    // Fail gracefully - return empty recent searches
    return {
      recentSearches: [],
      limit: limit || 20,
      offset: 0,
      total: 0,
    }
  }
}

const recentSearchQuery = makeOptionallyAuthenticatedQuery<
  [RecentSearchParams],
  PaginatedRecentSearchDto
>('recentSearch', getRecentSearchData, {
  staleTime: 5 * 60 * 1000, // 5 minutes - recent searches change frequently
})

const createRecentSearch = async (
  accessToken: string | null,
  createRecentSearchDto: CreateRecentSearchDto
): Promise<Return> => {
  const { status, statusText } =
    await getRecentSearchesClient().createRecentSearch(
      { createRecentSearchDto },
      buildAxiosRequestConfig({ accessToken })
    )

  return { status, statusText }
}

const useCreateRecentSearch = (
  options?: Parameters<
    typeof useMutation<Return, Error, CreateRecentSearchDto>
  >[0]
) => {
  const accessToken = useAccessToken()
  return useMutation({
    mutationFn: (params: CreateRecentSearchDto) =>
      createRecentSearch(accessToken || '', params),
    ...options,
  })
}

export { recentSearchQuery, useCreateRecentSearch, createRecentSearch }
